import React from 'react';
import { Link } from 'react-router-dom';
import { fileUrl } from '@/lib/api';

const SIZE_PX = { xs: 32, sm: 40, md: 64, lg: 128, xl: 256 };

/**
 * AvatarFrame — Orokin gilded oval frame around a user avatar.
 *
 * Props:
 *   user: { username, display_name, avatar_file_id }
 *   size: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
 *   variant: 'none' | 'gilded_oval' | 'pillared' | 'laurel'
 *   overlayUrl: string | null  (custom user-uploaded transparent overlay)
 *   to: optional link target; defaults to /u/{username}
 *   showMonogram: boolean (only on lg/xl)
 */
export default function AvatarFrame({
  user, size = 'sm', variant = 'gilded_oval', overlayUrl = null,
  to, className = '', dataTestId,
}) {
  const px = SIZE_PX[size] || SIZE_PX.sm;
  const username = user?.username || 'unknown';
  const initials = (user?.display_name || username || '?').slice(0, 2).toUpperCase();
  const linkTo = to !== undefined ? to : `/u/${username}`;
  const isLarge = size === 'lg' || size === 'xl';

  // Compose ornament classes based on variant
  const trim =
    variant === 'none'
      ? 'border border-[hsl(var(--app-border))]'
      : 'border border-[hsl(var(--gold)/0.70)]';
  const ringShadow =
    variant === 'none'
      ? ''
      : 'shadow-[0_0_0_1px_hsl(var(--gold)/0.22),0_8px_22px_hsl(222_34%_12%/0.10)]';

  const inner = (
    <span
      className={`relative inline-block overflow-hidden rounded-full bg-[hsl(var(--app-card-2))] text-foreground ${trim} ${ringShadow}`}
      style={{ width: px, height: px }}
      data-testid={dataTestId || 'avatar-frame'}
      aria-label={username}
    >
      {user?.avatar_file_id ? (
        <img src={fileUrl(user.avatar_file_id)} alt="" className="h-full w-full object-cover" />
      ) : (
        <span className="flex h-full w-full items-center justify-center font-mono text-xs text-muted-foreground" style={{ fontSize: Math.max(10, px / 4) }}>
          {initials}
        </span>
      )}
      {variant !== 'none' && (
        <span
          className="pointer-events-none absolute inset-0"
          style={{ background: 'radial-gradient(120% 120% at 30% 20%, hsl(var(--gold) / 0.18), transparent 55%)' }}
          aria-hidden
        />
      )}
      {overlayUrl && (
        <img src={overlayUrl} alt="" className="pointer-events-none absolute inset-0 h-full w-full object-cover" />
      )}
      {variant === 'pillared' && isLarge && (
        <span className="pointer-events-none absolute inset-0" aria-hidden>
          <span className="absolute left-0 top-1/2 h-2/3 w-[3px] -translate-y-1/2 rounded bg-[hsl(var(--gold)/0.55)]" />
          <span className="absolute right-0 top-1/2 h-2/3 w-[3px] -translate-y-1/2 rounded bg-[hsl(var(--gold)/0.55)]" />
        </span>
      )}
      {variant === 'laurel' && isLarge && (
        <span className="pointer-events-none absolute inset-0" aria-hidden>
          <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full">
            <path d="M10 50 Q50 -5 90 50" stroke="hsl(var(--gold) / 0.55)" strokeWidth="2" fill="none" />
            <path d="M10 50 Q50 105 90 50" stroke="hsl(var(--gold) / 0.55)" strokeWidth="2" fill="none" />
          </svg>
        </span>
      )}
    </span>
  );
  if (linkTo === null || linkTo === false) {
    return <span className={className}>{inner}</span>;
  }
  return <Link to={linkTo} className={className}>{inner}</Link>;
}

export const AVATAR_FRAME_VARIANTS = ['none', 'gilded_oval', 'pillared', 'laurel'];
